import { useState, type ReactNode } from 'react';

import {
  Alert,
  Avatar,
  Badge,
  Box,
  Button as MuiButton,
  Checkbox,
  Chip,
  type ChipProps,
  CircularProgress,
  Dialog,
  FormControlLabel,
  IconButton,
  Radio,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

import { PhoneScreenContext } from '@/designs-dev/device/PhoneScreenContext';
import { Button } from '@karehero/llama/components/atoms/Button/Button';
import { Card as LlamaCard } from '@karehero/llama/components/atoms/Card/Card';
import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';
import { Loader } from '@karehero/llama/components/atoms/Loader/Loader';
import { Logo } from '@karehero/llama/components/atoms/Logo/Logo';
import { ProgressBar } from '@karehero/llama/components/atoms/ProgressBar/ProgressBar';

import ActionCard, { type Item } from '@/components/wrapped/ActionCard';
import Card from '@/components/wrapped/Card';
import ChatActionRow from '@/components/wrapped/ChatActionRow';
import Drawer from '@/components/wrapped/Drawer';
import KeniOrb from '@/components/wrapped/KeniOrb';
import NotificationRow from '@/components/wrapped/NotificationRow';
import OptionCard from '@/components/wrapped/OptionCard';
import ErrorState from '@/components/mui/ErrorState';
import SearchField from '@/components/mui/SearchField';
import ShortlistModal from '@/components/mui/ShortlistModal';
import ShortlistSelect from '@/components/mui/ShortlistSelect';
import { type ComponentEntry } from '@/designs-dev/audit/types';
import { FORCED } from '@/designs-dev/library/forceStates';
import BottomSheet from '@/designs-dev/device/BottomSheet';
import { inFrameDialogProps } from '@/designs-dev/device/inFrameDialog';
import TabBar, { DEFAULT_ITEMS } from '@/designs-dev/device/TabBar';
import AccountBar from '@/karehero-app/access/AccountBar';
import ProviderCard from '@/karehero-app/care/shortlists/ProviderCard';
import { shortlistById } from '@/karehero-app/care/shortlists/fixtures';
import ArticleCard from '@/karehero-app/home/ArticleCard';
import AppBar from '@/karehero-app/shell/AppBar';
import { MenuRow } from '@/karehero-app/shell/CircleSwitcher';
import Navigation from '@/karehero-app/shell/Navigation';
import CalculatorBar from '@/karehero-app/tell-us-once/benefits/CalculatorBar';
import { onboarding20Components } from '@/karehero-app/tell-us-once/audit/onboarding20Components';
import ClaimRow from '@/karehero-app/tell-us-once/intake/ClaimRow';
import FactList from '@/karehero-app/tell-us-once/intake/FactList';
import { REVIEW } from '@/karehero-app/tell-us-once/intake/content';

// The library's contents: every component the prototypes use, grouped by type,
// each drawn from the real code with made-up props. The sample values here are
// placeholders for drawing, not copy.

/** Two properties of one component, one down the side and one across the top. */
export type Matrix = {
  title?: string;
  columns: string[];
  rows: { label: string; cells: ReactNode[] }[];
};

/**
 * A component set. A grid where it has two properties worth crossing, like a
 * button's style and its state, and a row of variants for anything left over.
 */
export type LibraryEntry = {
  name: string;
  matrices?: Matrix[];
  states?: ComponentEntry['states'];
  /**
   * The component this one replaced, drawn under it and marked Old. Older
   * prototypes still use it, so it stays findable, beside what to use instead.
   */
  old?: LibraryEntry;
};

export type LibraryGroup = { title: string; components: LibraryEntry[] };

/** A page of the library, the way Storybook's sidebar will have it. */
export type LibraryPage = {
  title: string;
  sections: { title?: string; groups: LibraryGroup[] }[];
};

const noop = () => {};

/** A phone's content width, for anything that fills its row. */
const PHONE_W = 343;
/** A whole phone's width, for bars that run edge to edge. */
const SCREEN_W = 375;

const Phone = ({ children }: { children: ReactNode }) => (
  <Box sx={{ width: PHONE_W }}>{children}</Box>
);

const Edge = ({ children }: { children: ReactNode }) => (
  <Box sx={{ width: SCREEN_W, bgcolor: 'background.default' }}>{children}</Box>
);

/**
 * A positioned box for an overlay to sit in. Sheets and drawers anchor to the
 * nearest positioned parent, and the transform holds a dialog's fixed position
 * here too, so none of them cover the canvas.
 */
const Stage = ({
  width = SCREEN_W,
  height,
  children,
}: {
  width?: number;
  height: number;
  children: ReactNode;
}) => {
  // Its own screen, so a sheet that portals to the screen lands here rather
  // than on the whole page, which is a screen too while drill mode reads it.
  const [el, setEl] = useState<HTMLElement | null>(null);
  return (
    <Box
      ref={setEl}
      sx={{
        position: 'relative',
        transform: 'translateZ(0)',
        width,
        height,
        overflow: 'hidden',
        bgcolor: 'background.default',
        border: 1,
        borderColor: 'divider',
      }}
    >
      <PhoneScreenContext.Provider value={el}>
        {children}
      </PhoneScreenContext.Provider>
    </Box>
  );
};

/** The states onboarding 2.0's components page already draws, reused. */
const onboarding = (name: string) =>
  onboarding20Components.find((c) => c.name === name)?.states ?? [];

const action: Item = {
  id: 1,
  title: 'Upload your bank statement',
  overdue: true,
  schedule: 'Due Sat 24th May · 16:00',
  who: 'OP',
  createdBy: 'Abbie',
  files: [],
  kind: 'cam',
  done: false,
};

const providers = shortlistById('residential-nursing').providers;

const shortlists = [
  { id: 'a', name: 'Residential, near Leeds', status: 'published' as const },
  { id: 'b', name: 'Home care', status: 'draft' as const },
];

const ICONS = [
  'house',
  'calendar',
  'user',
  'heart',
  'circle-question',
  'bell',
  'bars',
  'xmark',
  'check',
  'circle-check',
  'chevron-left',
  'chevron-right',
  'chevron-down',
  'plus',
  'pencil',
  'trash',
  'ellipsis-vertical',
  'magnifying-glass',
  'phone',
  'message',
  'microphone',
  'camera',
  'paper-plane-top',
  'file-lines',
  'file-contract',
  'id-card',
  'user-group',
  'user-headset',
  'shield-check',
  'sparkles',
  'star',
  'clock',
  'calculator',
  'circle-info',
  'circle-exclamation',
  'exclamation-triangle',
  'hand-holding-heart',
  'arrow-right-from-bracket',
];

const ICON_COLUMNS = 8;

const BUTTON_STYLES = [
  'Primary',
  'Secondary',
  'Link',
  'Coral',
  'Dashed',
] as const;
const BUTTON_STATES = [
  'Default',
  'Hover',
  'Pressed',
  'Focused',
  'Disabled',
] as const;

/** The class that puts a cell in its state. Disabled is a prop, not a class. */
const STATE_CLASS: Record<(typeof BUTTON_STATES)[number], string | undefined> =
  {
    Default: undefined,
    Hover: FORCED.hover,
    Pressed: FORCED.pressed,
    Focused: 'Mui-focusVisible',
    Disabled: undefined,
  };

const styledButton = (
  style: (typeof BUTTON_STYLES)[number],
  state: (typeof BUTTON_STATES)[number],
) => {
  const forced = STATE_CLASS[state];
  const disabled = state === 'Disabled';
  // Coral and dashed are theme classes on MUI's Button, which llama's does not
  // reach. Same button underneath.
  const classes = (base: string) => [base, forced].filter(Boolean).join(' ');
  switch (style) {
    case 'Coral':
      return (
        <MuiButton
          variant='contained'
          className={classes('coral')}
          disabled={disabled}
        >
          Start
        </MuiButton>
      );
    case 'Dashed':
      return (
        <MuiButton
          variant='outlined'
          className={classes('dashed')}
          disabled={disabled}
        >
          Upload document
        </MuiButton>
      );
    default:
      return (
        <Button
          variant={
            style === 'Primary'
              ? 'primary'
              : style === 'Secondary'
                ? 'secondary'
                : 'link'
          }
          className={forced}
          isDisabled={disabled}
          ariaLabel={style}
        >
          {style === 'Primary'
            ? 'Continue'
            : style === 'Secondary'
              ? 'Not now'
              : 'Skip'}
        </Button>
      );
  }
};

const BUTTON_SIZES = [
  { label: 'Small', size: 'sm' },
  { label: 'Medium', size: 'md' },
  { label: 'Large', size: 'lg' },
] as const;
const BUTTON_CONTENTS = [
  'Text',
  'Icon left',
  'Icon right',
  'Icon only',
] as const;

const sizedButton = (
  content: (typeof BUTTON_CONTENTS)[number],
  size: (typeof BUTTON_SIZES)[number]['size'],
) => (
  <Button
    size={size}
    ariaLabel='Add'
    iconLeft={
      content === 'Icon left' || content === 'Icon only'
        ? ('plus' as never)
        : undefined
    }
    iconRight={content === 'Icon right' ? ('plus' as never) : undefined}
  >
    {content === 'Icon only' ? undefined : 'Add'}
  </Button>
);

/** A grid of cells: columns across the top, each row its label and its cells. */
const grid = (
  columns: readonly string[],
  rows: [string, ReactNode[]][],
  title?: string,
): Matrix => ({
  title,
  columns: [...columns],
  rows: rows.map(([label, cells]) => ({ label, cells })),
});

/** The states of a control that is not a button: there is no pressed style. */
const CONTROL_STATES = ['Default', 'Hover', 'Focused', 'Disabled'] as const;

const ICON_BUTTONS = [
  { label: 'Medium', icon: 'ellipsis-vertical', size: 'medium', badge: false },
  { label: 'Small', icon: 'xmark', size: 'small', badge: false },
  { label: 'With badge', icon: 'bell', size: 'medium', badge: true },
] as const;

const FIELD_STATES = [
  'Empty',
  'Filled',
  'Focused',
  'Error',
  'Disabled',
] as const;
const FIELD_SIZES = [
  { label: 'Medium', size: 'medium' },
  { label: 'Small', size: 'small' },
] as const;

const CHIP_COLORS: { label: string; color: ChipProps['color'] }[] = [
  { label: 'Default', color: 'default' },
  { label: 'Primary', color: 'primary' },
  { label: 'Lime Green', color: 'limeGreen' },
  { label: 'Warm Orange', color: 'warmOrange' },
  { label: 'Sunflower', color: 'sunflower' },
  { label: 'Cherry', color: 'cherry' },
  { label: 'Success', color: 'success' },
  { label: 'Info', color: 'info' },
  { label: 'Error', color: 'error' },
];

/**
 * The two round icon buttons: the white circle on the bars (back, close, info,
 * the bell) and the coral one that sends and records in the chat. Coral is the
 * theme's `chatControl`, which the chat's own send button matches by hand.
 */
const ROUND_BUTTONS = [
  {
    label: 'White',
    style: 'white',
    icons: [
      { label: 'back', icon: 'chevron-left' },
      { label: 'close', icon: 'xmark' },
      { label: 'info', icon: 'circle-info' },
      { label: 'notifications', icon: 'bell' },
    ],
  },
  {
    label: 'Coral',
    style: 'coral',
    icons: [
      { label: 'send', icon: 'paper-plane-top' },
      { label: 'record', icon: 'microphone' },
    ],
  },
] as const;

const roundIconButton = (
  style: (typeof ROUND_BUTTONS)[number]['style'],
  icon: string,
  state: (typeof CONTROL_STATES)[number],
) => (
  <IconButton
    aria-label={icon}
    variant={style === 'coral' ? 'chatControl' : 'round'}
    className={STATE_CLASS[state]}
    disabled={state === 'Disabled'}
  >
    <Icon icon={icon as never} fontSize={16} />
  </IconButton>
);

const TABS = DEFAULT_ITEMS;

// Every entry, by type. The pages below take them from here by name, so an
// entry is written once whichever page it lands on.
const BY_TYPE: LibraryGroup[] = [
  {
    title: 'Buttons',
    components: [
      {
        name: 'Button',
        matrices: [
          {
            columns: [...BUTTON_STATES],
            rows: BUTTON_STYLES.map((style) => ({
              label: style,
              cells: BUTTON_STATES.map((state) => styledButton(style, state)),
            })),
          },
          {
            title: 'Sizes',
            columns: BUTTON_SIZES.map((s) => s.label),
            rows: BUTTON_CONTENTS.map((content) => ({
              label: content,
              cells: BUTTON_SIZES.map((s) => sizedButton(content, s.size)),
            })),
          },
        ],
        states: [
          {
            label: 'Full width',
            node: (
              <Phone>
                <Button isFullWidth ariaLabel='Continue'>
                  Continue
                </Button>
              </Phone>
            ),
          },
        ],
      },
      {
        name: 'IconButton',
        matrices: [
          grid(
            CONTROL_STATES,
            ICON_BUTTONS.map((b) => [
              b.label,
              CONTROL_STATES.map((state) => (
                <IconButton
                  aria-label={b.icon}
                  size={b.size}
                  className={STATE_CLASS[state]}
                  disabled={state === 'Disabled'}
                >
                  {b.badge ? (
                    <Badge color='error' variant='dot'>
                      <Icon icon={b.icon as never} fontSize={18} />
                    </Badge>
                  ) : (
                    <Icon icon={b.icon as never} fontSize={18} />
                  )}
                </IconButton>
              )),
            ]),
          ),
        ],
      },
      {
        name: 'Round icon button',
        matrices: [
          grid(
            CONTROL_STATES,
            ROUND_BUTTONS.map((b) => [
              b.label,
              CONTROL_STATES.map((state) =>
                roundIconButton(b.style, b.icons[0].icon, state),
              ),
            ]),
          ),
        ],
        states: ROUND_BUTTONS.flatMap((b) =>
          b.icons.map(({ label, icon }) => ({
            label: `${b.label}, ${label}`,
            node: roundIconButton(b.style, icon, 'Default'),
          })),
        ),
      },
    ],
  },
  {
    title: 'Inputs',
    components: [
      {
        name: 'TextField',
        matrices: [
          grid(
            FIELD_STATES,
            FIELD_SIZES.map((size) => [
              size.label,
              FIELD_STATES.map((state) => (
                <Phone>
                  <TextField
                    fullWidth
                    size={size.size}
                    label='Full name'
                    defaultValue={
                      state === 'Empty' ? undefined : 'Margaret Price'
                    }
                    focused={state === 'Focused' || undefined}
                    error={state === 'Error'}
                    helperText={state === 'Error' ? 'Error text' : undefined}
                    disabled={state === 'Disabled'}
                  />
                </Phone>
              )),
            ]),
          ),
        ],
      },
      {
        name: 'SearchField',
        states: [
          {
            label: 'Small',
            node: (
              <Phone>
                <SearchField placeholder='Search' size='small' />
              </Phone>
            ),
          },
          {
            label: 'Extra small',
            node: (
              <Phone>
                <SearchField placeholder='Search' />
              </Phone>
            ),
          },
        ],
      },
      {
        name: 'ShortlistSelect',
        states: [
          {
            label: 'Published',
            node: (
              <Phone>
                <ShortlistSelect
                  lists={shortlists}
                  value='a'
                  onChange={noop}
                  onCreate={noop}
                  labels={{
                    draft: 'Draft',
                    published: 'Published',
                    create: 'Create shortlist',
                  }}
                />
              </Phone>
            ),
          },
          {
            label: 'Draft',
            node: (
              <Phone>
                <ShortlistSelect
                  lists={shortlists}
                  value='b'
                  onChange={noop}
                  onCreate={noop}
                  labels={{
                    draft: 'Draft',
                    published: 'Published',
                    create: 'Create shortlist',
                  }}
                />
              </Phone>
            ),
          },
        ],
      },
      {
        name: 'Checkbox',
        matrices: [
          grid(
            CONTROL_STATES,
            (['Off', 'On'] as const).map((value) => [
              value,
              CONTROL_STATES.map((state) => (
                <Checkbox
                  color='primary'
                  checked={value === 'On'}
                  className={STATE_CLASS[state]}
                  disabled={state === 'Disabled'}
                />
              )),
            ]),
          ),
        ],
        states: [
          {
            label: 'With label',
            node: (
              <FormControlLabel
                control={<Checkbox color='primary' checked />}
                label='Label'
              />
            ),
          },
        ],
      },
      {
        name: 'Radio',
        matrices: [
          grid(
            CONTROL_STATES,
            (['Off', 'On'] as const).map((value) => [
              value,
              CONTROL_STATES.map((state) => (
                <Radio
                  color='primary'
                  checked={value === 'On'}
                  className={STATE_CLASS[state]}
                  disabled={state === 'Disabled'}
                />
              )),
            ]),
          ),
        ],
        states: [
          {
            label: 'With label',
            node: (
              <FormControlLabel
                control={<Radio color='primary' checked />}
                label='Label'
              />
            ),
          },
        ],
      },
      {
        name: 'ToggleButtonGroup',
        states: [
          {
            label: 'Segmented',
            node: (
              <ToggleButtonGroup
                className='segmented'
                exclusive
                size='small'
                value='list'
              >
                <ToggleButton value='list'>List</ToggleButton>
                <ToggleButton value='map'>Map</ToggleButton>
              </ToggleButtonGroup>
            ),
          },
        ],
      },
    ],
  },
  {
    title: 'Chips and replies',
    components: [
      {
        name: 'Chip',
        matrices: [
          grid(
            ['Filled', 'Outlined', 'Deletable'],
            CHIP_COLORS.map((c) => [
              c.label,
              [
                <Chip size='small' color={c.color} label='Label' />,
                <Chip
                  size='small'
                  color={c.color}
                  variant='outlined'
                  label='Label'
                />,
                <Chip
                  size='small'
                  color={c.color}
                  label='Label'
                  onDelete={noop}
                />,
              ],
            ]),
          ),
        ],
      },
      { name: 'QuickReplies', states: onboarding('QuickReplies') },
    ],
  },
  {
    title: 'Cards',
    components: [
      {
        name: 'Card',
        matrices: [
          grid(
            ['Plain', 'Tappable', 'Progress and action'],
            (['default', 'compact'] as const).map((variant) => [
              variant === 'default' ? 'Default' : 'Compact',
              [
                <Phone>
                  <Card
                    variant={variant}
                    title='Care assessment'
                    subtitle='12 of 16 answered'
                  />
                </Phone>,
                <Phone>
                  <Card
                    variant={variant}
                    title='Care assessment'
                    subtitle='12 of 16 answered'
                    onClick={noop}
                  />
                </Phone>,
                <Phone>
                  <Card
                    variant={variant}
                    title='Care assessment'
                    subtitle='12 of 16 answered'
                    progress={75}
                    primaryAction={{ label: 'Continue', onClick: noop }}
                  />
                </Phone>,
              ],
            ]),
          ),
        ],
      },
      {
        name: 'LlamaCard',
        states: [
          {
            label: 'Default',
            node: (
              <Phone>
                <LlamaCard isFullWidth>
                  <Typography variant='h6'>Title</Typography>
                  <Typography variant='body2'>Body text</Typography>
                </LlamaCard>
              </Phone>
            ),
          },
          {
            label: 'Small padding',
            node: (
              <Phone>
                <LlamaCard isFullWidth isPaddingSm>
                  <Typography variant='h6'>Title</Typography>
                  <Typography variant='body2'>Body text</Typography>
                </LlamaCard>
              </Phone>
            ),
          },
        ],
      },
      {
        name: 'ActionCard',
        matrices: [
          grid(
            ['Overdue', 'Due', 'No date'],
            [false, true].map((done) => [
              done ? 'Done' : 'To do',
              [
                { overdue: true },
                { overdue: false },
                { overdue: false, schedule: undefined },
              ].map((when) => (
                <Phone>
                  <ActionCard
                    action={{ ...action, ...when, done }}
                    onToggle={noop}
                    onOpen={noop}
                  />
                </Phone>
              )),
            ]),
          ),
        ],
      },
      {
        name: 'OptionCard',
        matrices: [
          grid(
            ['Not selected', 'Selected'],
            (['radio', 'check'] as const).map((control) => [
              control === 'radio' ? 'Radio' : 'Check',
              [false, true].map((selected) => (
                <Phone>
                  <OptionCard
                    control={control}
                    selected={selected}
                    onClick={noop}
                    label='Option'
                  />
                </Phone>
              )),
            ]),
          ),
        ],
      },
      {
        name: 'FromUsCard',
        states: onboarding('FromUsCard').map((s) => ({
          ...s,
          node: <Phone>{s.node}</Phone>,
        })),
      },
      {
        name: 'ProviderCard',
        states: [
          {
            label: providers[0].status,
            node: (
              <Phone>
                <ProviderCard provider={providers[0]} onOpen={noop} />
              </Phone>
            ),
          },
          {
            label: providers[1].status,
            node: (
              <Phone>
                <ProviderCard provider={providers[1]} onOpen={noop} />
              </Phone>
            ),
          },
          {
            label: providers[3].status,
            node: (
              <Phone>
                <ProviderCard provider={providers[3]} onOpen={noop} />
              </Phone>
            ),
          },
        ],
      },
      {
        name: 'ArticleCard',
        states: [
          {
            label: 'Default',
            node: (
              <Phone>
                <ArticleCard title='Article title' summary='Summary line' />
              </Phone>
            ),
          },
        ],
      },
    ],
  },
  {
    title: 'Rows and lists',
    components: [
      {
        name: 'ClaimRow',
        states: [
          {
            label: 'Default',
            node: (
              <Phone>
                <ClaimRow
                  icon='hand-holding-heart'
                  name='Attendance Allowance'
                  description='Description'
                  count='3 of 14 answered'
                />
              </Phone>
            ),
          },
          {
            label: 'Lead',
            node: (
              <Phone>
                <ClaimRow
                  icon='hand-holding-heart'
                  name='Attendance Allowance'
                  description='Description'
                  lead
                />
              </Phone>
            ),
          },
          {
            label: 'Lead, peach',
            node: (
              <Phone>
                <ClaimRow
                  icon='hand-holding-heart'
                  name='Attendance Allowance'
                  description='Description'
                  lead
                  filled='peach'
                />
              </Phone>
            ),
          },
          {
            label: 'Lead, teal',
            node: (
              <Phone>
                <ClaimRow
                  icon='hand-holding-heart'
                  name='Attendance Allowance'
                  description='Description'
                  lead
                  filled='teal'
                />
              </Phone>
            ),
          },
          {
            label: 'Cherry',
            node: (
              <Phone>
                <ClaimRow
                  icon='hand-holding-heart'
                  name='Attendance Allowance'
                  description='Description'
                  cherry
                />
              </Phone>
            ),
          },
        ],
      },
      {
        name: 'FactList',
        states: [
          {
            label: 'A group',
            node: (
              <Phone>
                <FactList groups={REVIEW.groups.slice(0, 1)} />
              </Phone>
            ),
          },
        ],
      },
      {
        name: 'NotificationRow',
        states: [
          {
            label: 'Unread',
            node: (
              <Phone>
                <NotificationRow
                  title='Title'
                  body='Body text'
                  when='2h ago'
                  read={false}
                  onOpen={noop}
                />
              </Phone>
            ),
          },
          {
            label: 'Read',
            node: (
              <Phone>
                <NotificationRow
                  title='Title'
                  body='Body text'
                  when='2h ago'
                  read
                  onOpen={noop}
                />
              </Phone>
            ),
          },
        ],
      },
      {
        name: 'MenuRow',
        states: [
          {
            label: 'Default',
            node: (
              <Phone>
                <MenuRow icon='user-group' label='Label' onClick={noop} />
              </Phone>
            ),
          },
        ],
      },
    ],
  },
  {
    title: 'Chat',
    components: [
      ...[
        'ChatBubble',
        'ChatMenuMessage',
        'VoiceNote',
        'Attachment',
        'ReadingBubble',
        'LiveCaptionCard',
      ].map((name) => ({ name, states: onboarding(name) })),
      {
        name: 'ChatOption',
        states: onboarding('ChatOption'),
        old: {
          name: 'ChatActionRow',
          matrices: [
            grid(
              ['Plain', 'Selectable', 'Selected'],
              (['offer', 'go'] as const).map((tone) => [
                tone === 'offer' ? 'Offer' : 'Go',
                [
                  { selectable: false, selected: false },
                  { selectable: true, selected: false },
                  { selectable: true, selected: true },
                ].map((pick) => (
                  <Phone>
                    <ChatActionRow
                      label='Label'
                      tone={tone}
                      icon={tone === 'offer' ? 'camera' : undefined}
                      onClick={noop}
                      {...pick}
                    />
                  </Phone>
                )),
              ]),
            ),
          ],
        },
      },
      {
        name: 'KeniOrb',
        states: [
          { label: 'Idle', node: <KeniOrb size={64} state='idle' /> },
          {
            label: 'Listening',
            node: <KeniOrb size={64} state='listening' />,
          },
          { label: 'Speaking', node: <KeniOrb size={64} state='speaking' /> },
        ],
      },
    ],
  },
  {
    title: 'Sheets and dialogs',
    components: [
      {
        name: 'BottomSheet',
        states: [
          {
            label: 'Hug',
            node: (
              <Stage height={420}>
                <BottomSheet title='Title' variant='hug' onClose={noop}>
                  <Typography variant='body1'>Body text</Typography>
                </BottomSheet>
              </Stage>
            ),
          },
          {
            label: 'With actions',
            node: (
              <Stage height={420}>
                <BottomSheet
                  title='Title'
                  variant='hug'
                  onClose={noop}
                  footer={
                    <Box sx={{ display: 'grid', gap: 2 }}>
                      <MuiButton variant='contained' size='large' fullWidth>
                        Primary
                      </MuiButton>
                      <MuiButton variant='outlined' size='large' fullWidth>
                        Secondary
                      </MuiButton>
                    </Box>
                  }
                >
                  <Typography variant='body1'>Body text</Typography>
                </BottomSheet>
              </Stage>
            ),
          },
          {
            label: 'Full',
            node: (
              <Stage height={420}>
                <BottomSheet title='Title' variant='full' onClose={noop}>
                  <Typography variant='body1'>Body text</Typography>
                </BottomSheet>
              </Stage>
            ),
          },
        ],
      },
      {
        name: 'Drawer',
        states: [
          {
            label: 'Open',
            node: (
              <Stage width={640} height={420}>
                <Drawer
                  open
                  title='Title'
                  subtitle='Subtitle'
                  onClose={noop}
                  footer={
                    <Button isFullWidth ariaLabel='Save'>
                      Save
                    </Button>
                  }
                >
                  <TextField fullWidth label='Field' />
                </Drawer>
              </Stage>
            ),
          },
        ],
      },
      {
        name: 'ShortlistModal',
        states: [
          {
            label: 'Create',
            node: (
              <Stage width={520} height={320}>
                <ShortlistModal
                  open
                  inFrame
                  mode='create'
                  initialName=''
                  onClose={noop}
                  onSubmit={noop}
                />
              </Stage>
            ),
          },
          {
            label: 'Edit',
            node: (
              <Stage width={520} height={320}>
                <ShortlistModal
                  open
                  inFrame
                  mode='edit'
                  initialName='Residential, near Leeds'
                  onClose={noop}
                  onSubmit={noop}
                />
              </Stage>
            ),
          },
        ],
      },
      {
        name: 'Dialog',
        states: [
          {
            label: 'Default',
            node: (
              <Stage width={520} height={240}>
                <Dialog open maxWidth='xs' fullWidth {...inFrameDialogProps}>
                  <Box sx={{ p: 3 }}>
                    <Typography variant='h4'>Title</Typography>
                    <Typography variant='body1'>Body text</Typography>
                  </Box>
                </Dialog>
              </Stage>
            ),
          },
        ],
      },
    ],
  },
  {
    title: 'Bars and navigation',
    components: [
      {
        name: 'Navigation',
        states: [
          {
            label: 'Logo only',
            node: (
              <Edge>
                <Navigation onNotifications={noop} avatarInitials='OP' />
              </Edge>
            ),
          },
          {
            label: 'With a name',
            node: (
              <Edge>
                <Navigation
                  title='Margaret'
                  onNotifications={noop}
                  avatarInitials='OP'
                />
              </Edge>
            ),
          },
        ],
      },
      {
        name: 'AppBar',
        states: [
          {
            label: 'Title',
            node: (
              <Edge>
                <AppBar title='Title' onBack={noop} />
              </Edge>
            ),
          },
          {
            label: 'Title, subtitle, action',
            node: (
              <Edge>
                <AppBar
                  title='Title'
                  subtitle='Subtitle'
                  onBack={noop}
                  action={
                    <IconButton aria-label='More' size='small'>
                      <Icon icon={'ellipsis-vertical' as never} fontSize={18} />
                    </IconButton>
                  }
                />
              </Edge>
            ),
          },
          {
            label: 'Back only',
            node: (
              <Edge>
                <AppBar onBack={noop} />
              </Edge>
            ),
          },
          {
            label: 'Left aligned',
            node: (
              <Edge>
                <AppBar title='Title' align='left' size='small' />
              </Edge>
            ),
          },
        ],
      },
      {
        name: 'CalculatorBar',
        states: [
          {
            label: 'Default',
            node: (
              <Edge>
                <CalculatorBar />
              </Edge>
            ),
          },
          {
            label: 'With back',
            node: (
              <Edge>
                <CalculatorBar back subtitle='Subtitle' />
              </Edge>
            ),
          },
        ],
      },
      {
        name: 'AccountBar',
        states: [
          {
            label: 'In the chat',
            node: (
              <Edge>
                <AccountBar onSignOut={noop} />
              </Edge>
            ),
          },
          {
            label: 'In the app',
            node: (
              <Edge>
                <AccountBar
                  inApp
                  initials='OP'
                  title='Margaret'
                  onNotifications={noop}
                />
              </Edge>
            ),
          },
        ],
      },
      {
        name: 'TabBar',
        matrices: [
          grid(
            TABS.map((t) => t.label),
            (['lime', 'neutral'] as const).map((tone) => [
              tone === 'lime' ? 'Lime' : 'Neutral',
              TABS.map((t) =>
                tone === 'lime' ? (
                  <Edge>
                    <TabBar active={t.id} />
                  </Edge>
                ) : (
                  // The neutral bar floats, so it needs a positioned frame.
                  <Stage height={120}>
                    <TabBar active={t.id} tone='neutral' />
                  </Stage>
                ),
              ),
            ]),
          ),
        ],
      },
    ],
  },
  {
    title: 'Feedback',
    components: [
      {
        name: 'Alert',
        states: (['success', 'info', 'warning', 'error'] as const).map(
          (severity) => ({
            label: severity[0].toUpperCase() + severity.slice(1),
            node: (
              <Phone>
                <Alert severity={severity}>Message</Alert>
              </Phone>
            ),
          }),
        ),
      },
      {
        name: 'ErrorState',
        states: [
          {
            label: 'With retry',
            node: (
              <Phone>
                <ErrorState title='Title' body='Body text' onRetry={noop} />
              </Phone>
            ),
          },
        ],
      },
      {
        name: 'ProgressBar',
        states: [
          {
            label: 'Started',
            node: (
              <Phone>
                <ProgressBar limit={3} progress={1} label='Progress' />
              </Phone>
            ),
          },
          {
            label: 'Done',
            node: (
              <Phone>
                <ProgressBar limit={3} progress={3} label='Progress' />
              </Phone>
            ),
          },
        ],
      },
      {
        name: 'Loader',
        states: [{ label: 'Default', node: <Loader /> }],
      },
      {
        name: 'CircularProgress',
        states: [
          { label: 'Default', node: <CircularProgress /> },
          { label: 'Small', node: <CircularProgress size={20} /> },
        ],
      },
    ],
  },
  {
    title: 'Icons and brand',
    components: [
      {
        name: 'Icon',
        states: [
          {
            label: 'In use',
            node: (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: `repeat(${ICON_COLUMNS}, 112px)`,
                  rowGap: 2,
                }}
              >
                {ICONS.map((icon) => (
                  <Box
                    key={icon}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 0.75,
                    }}
                  >
                    <Icon icon={icon as never} fontSize={20} />
                    <Typography
                      variant='caption'
                      sx={{
                        color: 'text.secondary',
                        textAlign: 'center',
                        wordBreak: 'break-word',
                      }}
                    >
                      {icon}
                    </Typography>
                  </Box>
                ))}
              </Box>
            ),
          },
        ],
      },
      {
        name: 'Logo',
        states: [
          { label: 'Full', node: <Logo variant='full' size='md' /> },
          { label: 'Text', node: <Logo variant='text' size='md' /> },
          { label: 'Symbol', node: <Logo variant='symbol' size='md' /> },
        ],
      },
      {
        name: 'Avatar',
        states: [
          { label: 'Initials', node: <Avatar>OP</Avatar> },
          {
            label: 'Small',
            node: <Avatar sx={{ width: 32, height: 32 }}>OP</Avatar>,
          },
        ],
      },
    ],
  },
];

const ALL = BY_TYPE.flatMap((g) => g.components);

/** An entry by name. Throws, so a renamed entry fails here, not as a gap. */
export const entry = (name: string): LibraryEntry => {
  const found = ALL.find((e) => e.name === name);
  if (!found) throw new Error(`No library entry called "${name}"`);
  return found;
};

const group = (title: string, names: string[]): LibraryGroup => ({
  title,
  components: names.map(entry),
});

/**
 * Llama: the design system, by how a component gets its look. MUI is plain MUI
 * styled by the theme; Compound is custom, built from MUI parts.
 */
export const LLAMA_MUI: LibraryPage = {
  title: 'MUI',
  sections: [
    {
      groups: [
        group('Buttons', ['Button', 'IconButton', 'Round icon button']),
        group('Inputs', [
          'TextField',
          'Checkbox',
          'Radio',
          'ToggleButtonGroup',
        ]),
        group('Chips', ['Chip']),
        group('Dialogs', ['Dialog']),
        group('Feedback', ['Alert', 'CircularProgress']),
        group('People', ['Avatar']),
      ],
    },
  ],
};

export const LLAMA_COMPOUND: LibraryPage = {
  title: 'Compound',
  sections: [
    {
      groups: [
        group('Inputs', ['SearchField']),
        group('Cards', ['Card', 'ActionCard', 'OptionCard', 'LlamaCard']),
        group('Rows', ['NotificationRow']),
        group('Sheets', ['BottomSheet', 'Drawer']),
        group('Bars and navigation', ['AppBar', 'Navigation', 'TabBar']),
        group('Feedback', ['ErrorState', 'ProgressBar', 'Loader']),
        group('Keni and brand', ['KeniOrb', 'Logo']),
      ],
    },
  ],
};

/** Where each feature component is used. */
const FEATURE_USE: Record<string, string> = {
  AccountBar: 'Onboarding',
  ArticleCard: 'Home',
  Attachment: 'Onboarding',
  CalculatorBar: 'Benefits calculator',
  ChatBubble: 'Onboarding',
  ChatMenuMessage: 'Onboarding',
  ChatOption: 'Onboarding',
  ClaimRow: 'Tell us once',
  FactList: 'Tell us once',
  FromUsCard: 'Onboarding',
  LiveCaptionCard: 'Onboarding',
  MenuRow: 'Circle switcher',
  ProviderCard: 'Care search',
  QuickReplies: 'Onboarding',
  ReadingBubble: 'Onboarding',
  ShortlistModal: 'Care search',
  ShortlistSelect: 'Care search',
  VoiceNote: 'Onboarding',
};

/**
 * Feature: made for one feature and not promoted to Llama yet. One A to Z
 * list, each saying where it is used. A long list means things are not being
 * promoted.
 */
export const FEATURE: LibraryPage = {
  title: 'Feature',
  sections: [
    {
      groups: Object.keys(FEATURE_USE)
        .sort()
        .map((name) => ({
          title: `Used in ${FEATURE_USE[name]}`,
          components: [entry(name)],
        })),
    },
  ],
};
